import { Directive, ElementRef, Input, SecurityContext, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeValue } from '@angular/platform-browser';
import { IImageConfig, IMAGES_RESOLUTIONS } from '../../utils/image-config';
import { BreakpointObserver } from '@angular/cdk/layout';
import { isNullOrUndefined, isValidString } from '@pineapplelab/util';
import { ImageSizeService } from '../../services/image-size/image-size.service';

@Directive({
	selector: '[dynamicImage]',
	standalone: true
})
export class DynamicImageDirective {

	@Input() baseImageSrc: string | SafeValue;
    @Input() cssClass: string;
    @Input() errorUrl: string = '';

    @Input() displayResolutions: number[] = undefined;
    @Input() isIcon: boolean = false;
	@Input() fallback: string = null;


    imageConfig: IImageConfig;

    breakpoint_xs = '(max-width: 450px)';
    breakpoint_sm = '(min-width: 451px) and (max-width: 800px)';
    breakpoint_md = '(min-width: 801px) and (max-width: 1024px)';
    breakpoint_lg = '(min-width: 1025px) and (max-width: 1200px)';
    breakpoint_xlg = '(min-width: 1201px)';

    resolutions: number[] = [];
    breakpointsMapping: { bkp: string, value: number }[];

    imageSrc: string;
    defaultSrc: string;
    bestSize: string = '';
    filename: string;
    extension: string;
    loadTry: number = 0;
    resolution: any = '';
    token: string = '';
    constructor(
        private image: ElementRef<HTMLImageElement>,
        private breakpointObserver: BreakpointObserver,
        private imageSizeService: ImageSizeService,
        private sanitizer: DomSanitizer
    ) {
        image.nativeElement.onerror = (event) => this.errorImage(event);
    }


    ngOnChanges(changes: SimpleChanges): void {
		if (isNullOrUndefined(changes['baseImageSrc']?.currentValue)) {
			this.image.nativeElement.src = changes['fallback']?.currentValue;
			return;
		}

		this.image.nativeElement.src = changes['baseImageSrc']?.currentValue ?? changes['fallback']?.currentValue;

		this.sanitizerUrl();
		this.imageConfig = this.imageSizeService.getInstanceByUrl(this.baseImageSrc.toString());

		this.resolutions = this.getAvailableResolutions();
		const url = this.baseImageSrc.toString();
		this.filename = url.substring(0, url.lastIndexOf('.'));
		this.extension = url.substring(url.lastIndexOf('.'));
		this.token = this.extension.includes('?') ? this.extension.substring(this.extension.lastIndexOf('?')) : '';

		this.breakpointObserver.observe([
			this.breakpoint_xs,
			this.breakpoint_sm,
			this.breakpoint_md,
			this.breakpoint_lg,
			this.breakpoint_xlg,
		]).subscribe(screen => {



			if (this.isIcon) {

				switch (true) {
					// ICONS
					case this.resolutions.some(x => x == IMAGES_RESOLUTIONS.is):
						this.resolution = IMAGES_RESOLUTIONS.is;
						break;

					case this.resolutions.some(x => x == IMAGES_RESOLUTIONS.im):
						this.resolution = IMAGES_RESOLUTIONS.im;
						break;

					case this.resolutions.some(x => x == IMAGES_RESOLUTIONS.il):
						this.resolution = IMAGES_RESOLUTIONS.il;
						break;

					case this.resolutions.some(x => x == IMAGES_RESOLUTIONS.xxs):
						this.resolution = IMAGES_RESOLUTIONS.xxs;
						break;

					case this.resolutions.some(x => x == IMAGES_RESOLUTIONS.xs):
						this.resolution = IMAGES_RESOLUTIONS.xs;
						break;

					default:
						this.resolution = this.imageConfig.defaultResolution;
						break;
				}

			} else {


				const resLowToHeight = this.resolutions.sort((a, b) => a - b);

				this.breakpointsMapping =
					[
						{ bkp: this.breakpoint_xs, value: IMAGES_RESOLUTIONS.xs },      //  xs: 400,
						{ bkp: this.breakpoint_sm, value: IMAGES_RESOLUTIONS.sm },      //  sm: 800,
						{ bkp: this.breakpoint_md, value: IMAGES_RESOLUTIONS.md },      //  md: 1000,
						{ bkp: this.breakpoint_lg, value: IMAGES_RESOLUTIONS.lg },      //  lg: 1200
						{ bkp: this.breakpoint_xlg, value: 1201 }
					].map(bkp => {

						let value = resLowToHeight.find(el => el >= bkp.value) || resLowToHeight[resLowToHeight.length - 1];
						return { ...bkp, value }

					})

				this.resolution = this.breakpointsMapping.find(({ bkp, value }) => screen.breakpoints[bkp])?.value || this.imageConfig.defaultResolution;
			}

			this.imageSrc = `${this.filename}_${this.resolution}.webp${this.token}`;
			this.image.nativeElement.src = this.imageSrc;
			this.loadTry = 0;

		});

    }

    errorImage(event: any) {

        if (this.baseImageSrc) {
            this.sanitizerUrl();
            // Resized variants are generated together, so if the webp is missing the others are too: go straight to the original
            if (this.loadTry == 0)
                this.imageSrc = this.baseImageSrc.toString();
            if (this.loadTry >= 1){
                this.imageSrc = this.errorUrl || '/assets/images/thumbnail.png'
            }
            this.loadTry++;
            this.image.nativeElement.src = this.imageSrc;
        }
    }

    /**
     * Resolutions the backend generates for this image (defaultResolution + config resolutions).
     * Requested displayResolutions are snapped to the smallest generated one that is >= the request,
     * so we never ask for a variant that doesn't exist.
     */
    private getAvailableResolutions(): number[] {
        const generated = [...new Set([this.imageConfig.defaultResolution, ...this.imageConfig.resolutions])].sort((a, b) => a - b);
        if (!this.displayResolutions?.length)
            return generated;

        const snap = (requested: number) => generated.find(res => res >= requested) ?? generated[generated.length - 1];
        return [...new Set(this.displayResolutions.map(snap))];
    }

    sanitizerUrl() {
        this.baseImageSrc = this.sanitizer.sanitize(SecurityContext.URL, this.baseImageSrc);
    }
}
